/**
 * The account, the people on it, the switches and the credentials.
 *
 * `.docs/19-client-portal-pages.md` §Before fan-out 2: "Data hooks in
 * `apps/web/src/api/client/` — one hook per REST route/read model, with
 * `queryKeys.client.*` entries; typed from `AppType`. Agents call hooks; they do
 * not write fetches."
 *
 * Every file in this folder obeys the same three rules and nothing else:
 *
 *   1. **One export per route.** A hook is named for what it answers, not for the
 *      verb — `useMembers()`, `useInviteMember()` — and the path appears once.
 *   2. **The key comes from `api/keys.ts`.** Never a literal array.
 *   3. **A write says which modules it disturbs.** `module` plus `alsoInvalidates`,
 *      because a page must not have to know that inviting somebody changes the seat
 *      count in two places.
 */

import type { ClientRole, NotificationPrefsInput, UpdateClientInput } from '@fareast/shared'

import { useApiMutation, useApiQuery } from '../hooks.js'
import { queryKeys } from '../keys.js'

/** `GET /api/clients/me` — the account: name, tier, currency, timezone, seats. */
export function useAccount() {
  return useApiQuery({
    key: queryKeys.clients.me(),
    request: (api) => api.rest.clients.me.$get(),
  })
}

/** `PATCH /api/clients/me` — the invoice details form on `settings.html`. */
export function useUpdateAccount() {
  return useApiMutation({
    module: 'clients',
    request: (api, body: UpdateClientInput) => api.rest.clients.me.$patch({ json: body }),
  })
}

/** `GET /api/clients/me/members` — members, pending invitations, seats. */
export function useMembers() {
  return useApiQuery({
    key: queryKeys.clients.members(),
    request: (api) => api.rest.clients.me.members.$get(),
  })
}

/** `POST /api/clients/me/invitations` — the invite dialog. */
export function useInviteMember() {
  return useApiMutation({
    module: 'clients',
    request: (api, body: { email: string; role: ClientRole }) =>
      api.rest.clients.me.invitations.$post({ json: body }),
  })
}

/** `POST /api/clients/me/invitations/:id/resend` — the same invitation, again. */
export function useResendInvitation() {
  return useApiMutation({
    module: 'clients',
    request: (api, id: string) =>
      api.rest.clients.me.invitations[':id'].resend.$post({ param: { id } }),
  })
}

/**
 * `DELETE /api/clients/me/invitations/:id` — takes the offer back. Answers `204`.
 *
 * Session 24 (R2-14). The seat-limit refusal has always said "Remove somebody,
 * cancel an invitation, or ask us to raise the limit" and the second of those did
 * not exist anywhere, so a mistyped address held a seat for its full seven days.
 * `clients` is the only module it disturbs, and that is what repaints the seat count
 * and the pending row together.
 */
export function useCancelInvitation() {
  return useApiMutation({
    module: 'clients',
    request: (api, id: string) => api.rest.clients.me.invitations[':id'].$delete({ param: { id } }),
  })
}

/** `PATCH /api/clients/me/members/:id` — the role dialog. Answers `204`. */
export function useSetMemberRole() {
  return useApiMutation({
    module: 'clients',
    request: (api, variables: { id: string; role: ClientRole }) =>
      api.rest.clients.me.members[':id'].$patch({
        param: { id: variables.id },
        json: { role: variables.role },
      }),
  })
}

/** `DELETE /api/clients/me/members/:id` — removing a seat. Answers `204`. */
export function useRemoveMember() {
  return useApiMutation({
    module: 'clients',
    request: (api, id: string) => api.rest.clients.me.members[':id'].$delete({ param: { id } }),
  })
}

/**
 * `PATCH /api/clients/me/timezone` — the signed-in person's own zone.
 *
 * `clients.update` is the **organisation's** zone and is a different call
 * (`CLAUDE.md` §Gotchas). This one is the member's, and it is what their call
 * reminders are worded in.
 *
 * **`null` clears it** and the account's zone stands in again — session 24 (R2-08).
 * The route takes it because `SetTimezoneSchema.timezone` is nullable; the SPA is
 * not faking an unset by writing the account's zone on to the seat, which would look
 * identical today and would stop following the account the day staff moved it.
 */
export function useSetMyTimezone() {
  return useApiMutation({
    module: 'clients',
    request: (api, timezone: string | null) =>
      api.rest.clients.me.timezone.$patch({ json: { timezone } }),
    alsoInvalidates: ['calls'],
  })
}

/** `GET /api/clients/me/notification-prefs` — the switches, per member. */
export function useNotificationPrefs() {
  return useApiQuery({
    key: queryKeys.clients.notificationPrefs(),
    request: (api) => api.rest.clients.me['notification-prefs'].$get(),
  })
}

/**
 * `PUT /api/clients/me/notification-prefs` — all of them at once.
 *
 * The route is a `PUT` because that is how the page edits them: seven switches and
 * one *Save changes*, and a partial write would make "what did I just save"
 * unanswerable (`modules/clients/routes.ts`).
 */
export function useSetNotificationPrefs() {
  return useApiMutation({
    module: 'clients',
    request: (api, body: NotificationPrefsInput) =>
      api.rest.clients.me['notification-prefs'].$put({ json: body }),
  })
}

/**
 * `GET /api/clients/me/credentials` — labels only. The plaintext is a mutation.
 *
 * `inline` by default: delegated access is one panel on the files page, and a client
 * who cannot read their credential list should still see their deliverables.
 */
export function useCredentials(options: { inline?: boolean } = {}) {
  return useApiQuery({
    key: queryKeys.clients.credentials(),
    request: (api) => api.rest.clients.me.credentials.$get(),
    inline: options.inline ?? true,
  })
}

/**
 * `POST /api/clients/me/credentials/:id/reveal` — the plaintext, once, audited.
 *
 * A mutation rather than a query because it writes an audit row, and because a
 * secret that lands in the query cache is a secret at rest in a tab somebody
 * leaves open. The answer is used and dropped.
 */
export function useRevealCredential() {
  return useApiMutation({
    module: 'clients',
    request: (api, id: string) =>
      api.rest.clients.me.credentials[':id'].reveal.$post({ param: { id } }),
  })
}
